const EDGE_URL = "https://pdxhoigcrxsqwxgrphdv.supabase.co/functions/v1/custom-auth";
const TOKEN_KEY = "custom_auth_token";
const USER_KEY = "custom_auth_user";
const EXPIRE_KEY = "custom_auth_expire"; // 过期时间戳（ms），不存在则为 session 模式

// 清除可能残留的旧 mock 缓存键
const LEGACY_KEYS = ["currentUser", "mockUser", "user_cache", "auth_user"];
LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));

export interface CustomUser {
  id: string;
  username: string;
  nickname: string;
  phone: string | null;
  email: string | null;
  avatar_url: string | null;
  level: string;
  performance: number;
  points: number;
  balance: number;
  status: string;
  created_at: string;
}

async function callAuth(body: object): Promise<{ user?: CustomUser; token?: string; error?: string }> {
  const res = await fetch(EDGE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return res.json();
}

export async function registerUser(params: {
  username: string;
  password: string;
  nickname: string;
  phone: string;
  inviteCode?: string;
}): Promise<{ user: CustomUser } | { error: string }> {
  const result = await callAuth({ action: "register", ...params });
  if (result.error) return { error: result.error };
  if (result.token && result.user) {
    localStorage.setItem(TOKEN_KEY, result.token);
    localStorage.setItem(USER_KEY, JSON.stringify(result.user));
    return { user: result.user };
  }
  return { error: "注册失败，请重试" };
}

export async function loginUser(params: {
  username: string;
  password: string;
  rememberMe?: boolean;
}): Promise<{ user: CustomUser } | { error: string }> {
  const { rememberMe, ...authParams } = params;
  const result = await callAuth({ action: "login", ...authParams });
  if (result.error) return { error: result.error };
  if (result.token && result.user) {
    localStorage.setItem(TOKEN_KEY, result.token);
    localStorage.setItem(USER_KEY, JSON.stringify(result.user));
    if (rememberMe) {
      // 7天后过期
      const expireAt = Date.now() + 7 * 24 * 60 * 60 * 1000;
      localStorage.setItem(EXPIRE_KEY, String(expireAt));
    } else {
      // session 模式：不设过期，关闭浏览器后 sessionStorage 自动清除
      localStorage.removeItem(EXPIRE_KEY);
      // 同时在 sessionStorage 标记本次 session 有效
      sessionStorage.setItem(TOKEN_KEY, result.token);
    }
    return { user: result.user };
  }
  return { error: "登录失败，请重试" };
}

export function logoutUser(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(EXPIRE_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
}

export function getStoredUser(): CustomUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as CustomUser) : null;
  } catch {
    return null;
  }
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * 检查登录态是否仍然有效：
 * - 有过期时间戳 → 检查是否超过7天
 * - 无过期时间戳（session模式）→ 检查 sessionStorage 是否还有 token
 */
export function isAuthValid(): boolean {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return false;

  const expireStr = localStorage.getItem(EXPIRE_KEY);
  if (expireStr) {
    // 记住我模式：检查是否过期
    return Date.now() < Number(expireStr);
  }
  // session 模式：检查 sessionStorage
  return !!sessionStorage.getItem(TOKEN_KEY);
}

// 从数据库拉取最新用户数据，始终以数据库为准，不 fallback 旧缓存
export async function refreshUserFromDB(): Promise<CustomUser | null> {
  const stored = getStoredUser();
  if (!stored) return null;

  try {
    const { createClient } = await import("@supabase/supabase-js");
    const client = createClient(
      import.meta.env.VITE_PUBLIC_SUPABASE_URL as string,
      import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string
    );
    const { data, error } = await client
      .from("custom_users")
      .select("id, username, nickname, phone, email, avatar_url, level, performance, points, balance, status, created_at")
      .eq("id", stored.id)
      .maybeSingle();

    if (!error && data) {
      const updated = data as CustomUser;
      // 强制覆盖缓存，确保积分等字段始终是数据库最新值
      localStorage.setItem(USER_KEY, JSON.stringify(updated));
      return updated;
    }
    // 数据库查询失败（非网络原因），清除缓存避免显示脏数据
    if (error) {
      console.warn("[customAuth] refreshUserFromDB error:", error.message);
    }
  } catch (e) {
    console.warn("[customAuth] refreshUserFromDB network error:", e);
  }
  // 网络完全断开时才 fallback 缓存，保证离线可用
  return stored;
}
